// src/pages/clients/ClientList.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Menu,
  IconButton,
  Tooltip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  Paper,
  Chip,
  Divider,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControlLabel,
  LinearProgress,
  TableSortLabel,
  Radio,
  RadioGroup
} from '@mui/material';
import {
  Edit as EditIcon,
  Block as BlockIcon,
  CheckCircle as ActiveIcon,
  MonetizationOn as DebtIcon,
  NetworkCheck as TrafficIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  PlayArrow as ExecuteIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Engineering as EngineeringIcon,
  Receipt as ReceiptIcon,
  CloudDownload as ImportMikrotikIcon,
  MoreVert as MoreIcon,
  ViewColumn as ViewColumnIcon,
  Wifi as PingIcon,
  Send as SendIcon,
  FileDownload as ExportIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ContractModal from '../../components/contracts/ContractModal';

// Definición de todas las columnas disponibles
const ALL_COLUMNS = [
  { id: 'fullName', label: 'Nombres', alwaysVisible: true },
  { id: 'queueName', label: 'Queue' },
  { id: 'ipAddress', label: 'IP' },
  { id: 'status', label: 'Estado' },
  { id: 'balance', label: 'Saldo a Favor' },
  { id: 'planName', label: 'Plan' },
  { id: 'routerName', label: 'Router' },
  { id: 'zoneName', label: 'Zona' },
  { id: 'accessPoint', label: 'AP / Sectorial' },
  { id: 'address', label: 'Dirección' },
  { id: 'coordinates', label: 'Coordenadas' },
];

const DEFAULT_VISIBLE_COLUMNS = ALL_COLUMNS.map(c => c.id);

const COLUMN_STORAGE_KEY = 'yinowisp_clientlist_columns';

const loadSavedColumns = () => {
  try {
    const saved = localStorage.getItem(COLUMN_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch (e) { /* ignore */ }
  return DEFAULT_VISIBLE_COLUMNS;
};

const ClientList = () => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [pingDialogOpen, setPingDialogOpen] = useState(false);
  const [clientForPing, setClientForPing] = useState(null);
  const [pingLoading, setPingLoading] = useState(false);
  const [pingResult, setPingResult] = useState(null);

  // Estado para la exportación de reportes
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [selectedExportColumns, setSelectedExportColumns] = useState([]);
  const [exportFormat, setExportFormat] = useState('excel');

  // Estado para el modal de contrato
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [contractClientId, setContractClientId] = useState(null);
  const [contractInitialTab, setContractInitialTab] = useState(0);

  // Estado para modal de Baja de servicio y Anexo 2
  const [deactivationModalOpen, setDeactivationModalOpen] = useState(false);
  const [clientForDeactivation, setClientForDeactivation] = useState(null);

  // Estado para manejar la selección y acciones masivas
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [selectedAction, setSelectedAction] = useState('');
  const [broadcastModalOpen, setBroadcastModalOpen] = useState(false);
  const [broadcastChannel, setBroadcastChannel] = useState('both');
  const [broadcastTestMode, setBroadcastTestMode] = useState(true);
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [broadcasting, setBroadcasting] = useState(false);
  const [broadcastResult, setBroadcastResult] = useState(null);

  // Estado para el filtrado y paginación
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Estado para detección de dispositivos móviles
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Estado para menú de acciones individuales por cliente (compartido)
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
  const [actionMenuClient, setActionMenuClient] = useState(null);

  // Estado para visibilidad de columnas
  const [visibleColumns, setVisibleColumns] = useState(loadSavedColumns);
  const [columnSettingsAnchor, setColumnSettingsAnchor] = useState(null);

  // Sincronizar las columnas de exportación con las columnas visibles actuales al abrir el modal
  useEffect(() => {
    if (exportModalOpen) {
      setSelectedExportColumns(visibleColumns);
    }
  }, [exportModalOpen, visibleColumns]);

  const isColumnVisible = (colId) => visibleColumns.includes(colId);

  const handleColumnToggle = (colId) => {
    const col = ALL_COLUMNS.find(c => c.id === colId);
    if (col?.alwaysVisible) return;
    setVisibleColumns(prev => {
      const next = prev.includes(colId)
        ? prev.filter(id => id !== colId)
        : [...prev, colId];
      localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const handleResetColumns = () => {
    setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
    localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(DEFAULT_VISIBLE_COLUMNS));
  };

  const handleActionMenuOpen = (event, client) => {
    setActionMenuAnchorEl(event.currentTarget);
    setActionMenuClient(client);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchorEl(null);
    setActionMenuClient(null);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Estado para los diálogos
  const [addClientModalOpen, setAddClientModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [massDeleteDialogOpen, setMassDeleteDialogOpen] = useState(false);
  const [selectedClientForDelete, setSelectedClientForDelete] = useState(null);

  // Estado para progreso de operaciones
  const [operationProgress, setOperationProgress] = useState({
    show: false,
    message: '',
    current: 0,
    total: 0
  });

  const navigate = useNavigate();

  const fetchZones = async () => {
    try {
      const response = await fetch('/api/zones', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setZones(data);
    } catch (error) {
      console.error('Error fetching zones:', error);
    }
  };

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const url = selectedZone
        ? `/api/clients?zone=${selectedZone}`
        : '/api/clients';

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al obtener lista de clientes');
      }

      const data = await response.json();
      setClients(data);
      setFilteredClients(data);
      setSelectedClientIds([]);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedZone]);

  const handlePingClient = (client) => {
    setClientForPing(client);
    setPingDialogOpen(true);
    setPingLoading(true);
    setPingResult(null);
    
    fetch(`/api/clients/${client.id}/ping`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    })
      .then(res => res.json())
      .then(data => {
        setPingResult(data);
        setPingLoading(false);
      })
      .catch(err => {
        console.error('Error in ping request:', err);
        setPingResult({
          success: false,
          online: false,
          message: 'Error de red o servidor al intentar comunicar con el router.'
        });
        setPingLoading(false);
      });
  };

  useEffect(() => {
    fetchZones();
    fetchClients();
  }, [selectedZone, fetchClients]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredClients(clients);
    } else {
      const lowercasedFilter = searchTerm.toLowerCase();
      const filtered = clients.filter(client => {
        return (
          (client.fullName && client.fullName.toLowerCase().includes(lowercasedFilter)) ||
          (client.queueName && client.queueName.toLowerCase().includes(lowercasedFilter)) ||
          (client.ipAddress && client.ipAddress.toLowerCase().includes(lowercasedFilter)) ||
          (client.address && client.address.toLowerCase().includes(lowercasedFilter)) ||
          (client.planName && client.planName.toLowerCase().includes(lowercasedFilter)) ||
          (client.routerName && client.routerName.toLowerCase().includes(lowercasedFilter)) ||
          (client.zoneName && client.zoneName.toLowerCase().includes(lowercasedFilter)) ||
          (client.accessPointName && client.accessPointName.toLowerCase().includes(lowercasedFilter)) ||
          (client.nodeName && client.nodeName.toLowerCase().includes(lowercasedFilter))
        );
      });
      setFilteredClients(filtered);
    }
    setPage(0);
  }, [searchTerm, clients]);

  const handleZoneChange = (event) => {
    setSelectedZone(event.target.value);
  };

  // Manejo de selección de clientes
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const visibleClients = filteredClients
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map(client => client.id);
      setSelectedClientIds(visibleClients);
      return;
    }
    setSelectedClientIds([]);
  };

  const handleClientSelect = (id) => {
    const selectedIndex = selectedClientIds.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selectedClientIds, id];
    } else {
      newSelected = selectedClientIds.filter(clientId => clientId !== id);
    }

    setSelectedClientIds(newSelected);
  };

  const isSelected = (id) => selectedClientIds.indexOf(id) !== -1;

  // Función para mostrar alertas mejoradas
  const showAlert = (message, type = 'info') => {
    // Aquí podrías usar una librería de notificaciones como react-hot-toast
    alert(message);
  };

  const showErrorDetails = (error, operation = 'operación') => {
    let message = `Error en ${operation}:\n\n`;

    if (error.message) {
      message += `Mensaje: ${error.message}\n`;
    }

    if (error.processingTime) {
      message += `Tiempo: ${error.processingTime}ms\n`;
    }

    if (error.mikrotikResult) {
      message += `\nResultado MikroTik: ${JSON.stringify(error.mikrotikResult, null, 2)}`;
    }

    console.error(`Error en ${operation}:`, error);
    alert(message);
  };

  // Componente de diálogo para eliminación individual - MEJORADO
  const DeleteClientDialog = ({ open, onClose, clientId }) => {
    const [deleteFromMikrotik, setDeleteFromMikrotik] = useState(true);
    const [forceDelete, setForceDelete] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [deleteProgress, setDeleteProgress] = useState('');
    const [summary, setSummary] = useState(null);
    const [loadingSummary, setLoadingSummary] = useState(false);

    // Reset cuando se abre/cierra el diálogo
    useEffect(() => {
      if (open) {
        console.log('DeleteClientDialog opened for client:', clientId);
        setErrorMessage('');
        setForceDelete(false);
        setDeleteFromMikrotik(true);
        setIsDeleting(false);
        setDeleteProgress('');
        setSummary(null);

        if (clientId) {
          const fetchSummary = async () => {
            setLoadingSummary(true);
            try {
              const res = await fetch(`/api/clients/${clientId}/deletion-summary`, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
              });
              if (res.ok) {
                const data = await res.json();
                setSummary(data);
              }
            } catch (err) {
              console.error('Error fetching deletion summary:', err);
            } finally {
              setLoadingSummary(false);
            }
          };
          fetchSummary();
        }
      }
    }, [open, clientId]);

    const handleConfirmDelete = useCallback(async () => {
      console.log('=== INICIANDO ELIMINACIÓN INDIVIDUAL ===');
      console.log('ClientId:', clientId);

      if (!clientId || isDeleting) {
        console.log('Abortando: no hay clientId o ya está eliminando');
        return;
      }

      setIsDeleting(true);
      setErrorMessage('');
      setDeleteProgress('Iniciando eliminación...');

      try {
        console.log('Eliminando cliente:', {
          clientId,
          deleteFromMikrotik,
          forceDelete
        });

        const url = `/api/clients/${clientId}?deleteFromMikrotik=${deleteFromMikrotik}&forceDelete=${forceDelete}`;
        console.log('URL de eliminación:', url);

        setDeleteProgress('Conectando al servidor...');

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minutos timeout

        const response = await fetch(url, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        console.log('Response status:', response.status);

        let data;
        try {
          data = await response.json();
        } catch (parseError) {
          console.error('Error parsing response:', parseError);
          data = { message: 'Error en la respuesta del servidor' };
        }

        console.log('Respuesta del servidor:', data);

        if (!response.ok) {
          if (response.status === 400 && !forceDelete) {
            setErrorMessage(
              `${data.message}\n\nActive "Forzar eliminación" para continuar.`
            );
            return;
          }
          if (response.status === 408) {
            setErrorMessage('La operación tardó demasiado tiempo. Intente nuevamente o active "Forzar eliminación".');
            return;
          }
          throw new Error(data.message || `Error ${response.status}: ${response.statusText}`);
        }

        setDeleteProgress('Cliente eliminado exitosamente');

        // Actualizar la lista local
        setClients(prev => {
          console.log('Actualizando lista de clientes, removiendo:', clientId);
          return prev.filter(c => c.id !== clientId);
        });
        setFilteredClients(prev => prev.filter(c => c.id !== clientId));

        // Mostrar detalles del resultado
        let successMessage = 'Cliente eliminado exitosamente';
        if (data.processingTime) {
          successMessage += `\nTiempo de procesamiento: ${data.processingTime}ms`;
        }
        if (data.dependenciesDeleted) {
          const deps = Object.entries(data.dependenciesDeleted)
            .filter(([key, value]) => value > 0)
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ');
          if (deps) {
            successMessage += `\nDependencias eliminadas: ${deps}`;
          }
        }
        if (data.mikrotikResult && data.mikrotikResult.error) {
          successMessage += `\nAdvertencia MikroTik: ${data.mikrotikResult.error}`;
        }

        showAlert(successMessage, 'success');
        onClose();
      } catch (error) {
        console.error('Error deleting client:', error);

        if (error.name === 'AbortError') {
          setErrorMessage('La operación fue cancelada por timeout. Intente nuevamente.');
        } else {
          setErrorMessage(error.message || 'Error al eliminar el cliente');
        }

        setDeleteProgress('Error en la eliminación');
      } finally {
        setIsDeleting(false);
      }
    }, [clientId, deleteFromMikrotik, forceDelete, isDeleting, onClose]);

    const handleCancel = useCallback(() => {
      console.log('Cancelando eliminación individual');
      if (!isDeleting) {
        onClose();
      }
    }, [isDeleting, onClose]);

    return (
      <Dialog
        open={open}
        onClose={!isDeleting ? handleCancel : undefined}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={isDeleting}
      >
        <DialogTitle>
          Confirmar eliminación de cliente
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom sx={{ fontWeight: 500 }}>
              ¿Está seguro de que desea eliminar este cliente? Esta acción no se puede deshacer.
            </Typography>

            {loadingSummary ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: 3 }}>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Cargando resumen de registros asociados...
                </Typography>
              </Box>
            ) : summary ? (
              <Box sx={{ mt: 2, mb: 2, p: 2, border: '1px solid rgba(211, 47, 47, 0.2)', borderRadius: 2, bgcolor: 'rgba(211, 47, 47, 0.02)' }}>
                <Typography variant="subtitle2" color="error" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', mb: 1 }}>
                  <DeleteIcon sx={{ fontSize: 20, mr: 1 }} />
                  ADVERTENCIA DE ELIMINACIÓN PERMANENTE
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Esta acción eliminará de forma irreversible al cliente junto con todos los siguientes registros vinculados en la base de datos:
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
                  <Paper variant="outlined" sx={{ p: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderColor: summary.invoices > 0 ? 'warning.main' : 'divider', bgcolor: summary.invoices > 0 ? 'rgba(237, 108, 2, 0.03)' : 'transparent' }}>
                    <Typography variant="h6" color={summary.invoices > 0 ? 'warning.main' : 'text.primary'} sx={{ fontWeight: 'bold' }}>
                      {summary.invoices}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Facturas</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderColor: summary.payments > 0 ? 'error.main' : 'divider', bgcolor: summary.payments > 0 ? 'rgba(211, 47, 47, 0.03)' : 'transparent' }}>
                    <Typography variant="h6" color={summary.payments > 0 ? 'error.main' : 'text.primary'} sx={{ fontWeight: 'bold' }}>
                      {summary.payments}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Pagos y Abonos</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderColor: summary.equipment > 0 ? 'warning.main' : 'divider', bgcolor: summary.equipment > 0 ? 'rgba(237, 108, 2, 0.03)' : 'transparent' }}>
                    <Typography variant="h6" color={summary.equipment > 0 ? 'warning.main' : 'text.primary'} sx={{ fontWeight: 'bold' }}>
                      {summary.equipment}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Equipos Asignados</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderColor: summary.tickets > 0 ? 'info.main' : 'divider', bgcolor: summary.tickets > 0 ? 'rgba(2, 136, 209, 0.03)' : 'transparent' }}>
                    <Typography variant="h6" color={summary.tickets > 0 ? 'info.main' : 'text.primary'} sx={{ fontWeight: 'bold' }}>
                      {summary.tickets}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Tickets de Soporte</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderColor: summary.installations > 0 ? 'success.main' : 'divider', bgcolor: summary.installations > 0 ? 'rgba(46, 125, 50, 0.03)' : 'transparent' }}>
                    <Typography variant="h6" color={summary.installations > 0 ? 'success.main' : 'text.primary'} sx={{ fontWeight: 'bold' }}>
                      {summary.installations}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Instalaciones</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="h6" color="text.primary" sx={{ fontWeight: 'bold' }}>
                      {summary.trafficLogs}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Logs de Tráfico</Typography>
                  </Paper>
                </Box>
                {summary.notifications > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, textAlign: 'center' }}>
                    * Se eliminarán también {summary.notifications} notificaciones del historial.
                  </Typography>
                )}
              </Box>
            ) : null}

            {isDeleting && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <LinearProgress />
                <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                  {deleteProgress}
                </Typography>
              </Box>
            )}

            <FormControl component="fieldset" sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={deleteFromMikrotik}
                    onChange={(e) => {
                      console.log('Cambio deleteFromMikrotik:', e.target.checked);
                      setDeleteFromMikrotik(e.target.checked);
                    }}
                    color="primary"
                    disabled={isDeleting}
                  />
                }
                label="Eliminar también del router MikroTik"
              />
              <Typography variant="caption" color="text.secondary">
                Si desmarca esta opción, el cliente solo se eliminará de la base de datos pero seguirá existiendo en el router.
              </Typography>
            </FormControl>

            <FormControl component="fieldset" sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={forceDelete}
                    onChange={(e) => {
                      console.log('Cambio forceDelete:', e.target.checked);
                      setForceDelete(e.target.checked);
                    }}
                    color="warning"
                    disabled={isDeleting}
                  />
                }
                label="Forzar eliminación (incluso con facturas o equipos pendientes)"
              />
              <Typography variant="caption" color="error">
                ADVERTENCIA: Al marcar esta opción, se eliminará el cliente aunque tenga facturas o equipos pendientes. Las facturas serán eliminadas y los equipos se marcarán como devueltos.
              </Typography>
            </FormControl>

            {errorMessage && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.875rem' }}>
                  {errorMessage}
                </pre>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancel}
            color="primary"
            disabled={isDeleting}
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            {isDeleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Componente de diálogo para eliminación masiva - MEJORADO
  const MassDeleteDialog = ({ open, onClose }) => {
    const [deleteFromMikrotik, setDeleteFromMikrotik] = useState(true);
    const [forceDelete, setForceDelete] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [deleteProgress, setDeleteProgress] = useState('');
    const [summary, setSummary] = useState(null);
    const [loadingSummary, setLoadingSummary] = useState(false);

    // Reset cuando se abre/cierra el diálogo
    useEffect(() => {
      if (open) {
        console.log('MassDeleteDialog opened for clients:', selectedClientIds);
        setErrorMessage('');
        setForceDelete(false);
        setDeleteFromMikrotik(true);
        setIsDeleting(false);
        setDeleteProgress('');
        setSummary(null);

        if (selectedClientIds.length > 0) {
          const fetchSummary = async () => {
            setLoadingSummary(true);
            try {
              const res = await fetch('/api/clients/mass-deletion-summary', {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ clientIds: selectedClientIds })
              });
              if (res.ok) {
                const data = await res.json();
                setSummary(data);
              }
            } catch (err) {
              console.error('Error fetching mass deletion summary:', err);
            } finally {
              setLoadingSummary(false);
            }
          };
          fetchSummary();
        }
      }
    }, [open, selectedClientIds]);

    const handleConfirmDelete = useCallback(async () => {
      console.log('=== INICIANDO ELIMINACIÓN MASIVA ===');
      console.log('SelectedClientIds:', selectedClientIds);

      if (selectedClientIds.length === 0 || isDeleting) {
        console.log('Abortando: no hay clientes seleccionados o ya está eliminando');
        return;
      }

      setIsDeleting(true);
      setErrorMessage('');
      setDeleteProgress(`Preparando eliminación de ${selectedClientIds.length} clientes...`);

      try {
        console.log('Ejecutando eliminación masiva:', {
          clientIds: selectedClientIds,
          action: 'delete',
          deleteFromMikrotik,
          forceDelete
        });

        setDeleteProgress('Conectando al servidor...');

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 180000); // 3 minutos para operaciones masivas

        const response = await fetch('/api/clients/mass-action', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            clientIds: selectedClientIds,
            action: 'delete',
            deleteFromMikrotik: deleteFromMikrotik,
            forceDelete: forceDelete
          }),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        console.log('Response status:', response.status);

        let responseData;
        try {
          responseData = await response.json();
        } catch (parseError) {
          console.error('Error parsing response:', parseError);
          responseData = { message: 'Error en la respuesta del servidor' };
        }

        console.log('Respuesta del servidor:', responseData);

        if (!response.ok) {
          if (response.status === 400 && !forceDelete) {
            setErrorMessage(
              `${responseData.message}\n\nClientes afectados: ${responseData.dependentIds ? responseData.dependentIds.length : 'Varios'}\n\nActive "Forzar eliminación" para continuar.`
            );
            return;
          }
          if (response.status === 408) {
            setErrorMessage('La operación tardó demasiado tiempo. Intente con menos clientes o active "Forzar eliminación".');
            return;
          }
          throw new Error(responseData.message || `Error ${response.status}: ${response.statusText}`);
        }

        setDeleteProgress('Eliminación masiva completada exitosamente');

        // Mostrar detalles del resultado
        let successMessage = `${responseData.processed || selectedClientIds.length} clientes eliminados exitosamente`;
        if (responseData.processingTime) {
          successMessage += `\nTiempo de procesamiento: ${responseData.processingTime}ms`;
        }
        if (responseData.mikrotikResults) {
          const mikrotikErrors = responseData.mikrotikResults.filter(r => r.queueResult?.error || r.addressListResult?.error);
          if (mikrotikErrors.length > 0) {
            successMessage += `\nAdvertencias MikroTik en ${mikrotikErrors.length} clientes`;
          }
        }

        showAlert(successMessage, 'success');

        // Refrescar la lista de clientes
        console.log('Refrescando lista de clientes...');
        await fetchClients();
        setSelectedAction('');
        setSelectedClientIds([]);
        onClose();
      } catch (error) {
        console.error('Error ejecutando eliminación masiva:', error);

        if (error.name === 'AbortError') {
          setErrorMessage('La operación fue cancelada por timeout. Intente con menos clientes.');
        } else {
          setErrorMessage(error.message || 'Error al ejecutar eliminación masiva');
        }

        setDeleteProgress('Error en la eliminación masiva');
      } finally {
        setIsDeleting(false);
      }
    }, [selectedClientIds, deleteFromMikrotik, forceDelete, isDeleting, onClose, fetchClients]);

    const handleCancel = useCallback(() => {
      console.log('Cancelando eliminación masiva');
      if (!isDeleting) {
        onClose();
      }
    }, [isDeleting, onClose]);

    return (
      <Dialog
        open={open}
        onClose={!isDeleting ? handleCancel : undefined}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={isDeleting}
      >
        <DialogTitle>
          Confirmar eliminación masiva de clientes
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom sx={{ fontWeight: 500 }}>
              ¿Está seguro de que desea eliminar los {selectedClientIds.length} clientes seleccionados? Esta acción no se puede deshacer.
            </Typography>

            {loadingSummary ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: 3 }}>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Cargando resumen de registros asociados...
                </Typography>
              </Box>
            ) : summary ? (
              <Box sx={{ mt: 2, mb: 2, p: 2, border: '1px solid rgba(211, 47, 47, 0.2)', borderRadius: 2, bgcolor: 'rgba(211, 47, 47, 0.02)' }}>
                <Typography variant="subtitle2" color="error" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', mb: 1 }}>
                  <DeleteIcon sx={{ fontSize: 20, mr: 1 }} />
                  ADVERTENCIA DE ELIMINACIÓN PERMANENTE MASIVA
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Esta acción eliminará de forma irreversible a los <strong>{summary.clientsCount}</strong> clientes seleccionados junto con todos sus registros vinculados en la base de datos:
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
                  <Paper variant="outlined" sx={{ p: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderColor: summary.invoices > 0 ? 'warning.main' : 'divider', bgcolor: summary.invoices > 0 ? 'rgba(237, 108, 2, 0.03)' : 'transparent' }}>
                    <Typography variant="h6" color={summary.invoices > 0 ? 'warning.main' : 'text.primary'} sx={{ fontWeight: 'bold' }}>
                      {summary.invoices}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Facturas Totales</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderColor: summary.payments > 0 ? 'error.main' : 'divider', bgcolor: summary.payments > 0 ? 'rgba(211, 47, 47, 0.03)' : 'transparent' }}>
                    <Typography variant="h6" color={summary.payments > 0 ? 'error.main' : 'text.primary'} sx={{ fontWeight: 'bold' }}>
                      {summary.payments}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Pagos y Abonos</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderColor: summary.equipment > 0 ? 'warning.main' : 'divider', bgcolor: summary.equipment > 0 ? 'rgba(237, 108, 2, 0.03)' : 'transparent' }}>
                    <Typography variant="h6" color={summary.equipment > 0 ? 'warning.main' : 'text.primary'} sx={{ fontWeight: 'bold' }}>
                      {summary.equipment}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Equipos Asignados</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderColor: summary.tickets > 0 ? 'info.main' : 'divider', bgcolor: summary.tickets > 0 ? 'rgba(2, 136, 209, 0.03)' : 'transparent' }}>
                    <Typography variant="h6" color={summary.tickets > 0 ? 'info.main' : 'text.primary'} sx={{ fontWeight: 'bold' }}>
                      {summary.tickets}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Tickets de Soporte</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderColor: summary.installations > 0 ? 'success.main' : 'divider', bgcolor: summary.installations > 0 ? 'rgba(46, 125, 50, 0.03)' : 'transparent' }}>
                    <Typography variant="h6" color={summary.installations > 0 ? 'success.main' : 'text.primary'} sx={{ fontWeight: 'bold' }}>
                      {summary.installations}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Instalaciones Totales</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="h6" color="text.primary" sx={{ fontWeight: 'bold' }}>
                      {summary.trafficLogs}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Logs de Tráfico</Typography>
                  </Paper>
                </Box>
                {summary.notifications > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, textAlign: 'center' }}>
                    * Se eliminarán también {summary.notifications} notificaciones del historial.
                  </Typography>
                )}
              </Box>
            ) : null}

            {isDeleting && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <LinearProgress />
                <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                  {deleteProgress}
                </Typography>
              </Box>
            )}

            <FormControl component="fieldset" sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={deleteFromMikrotik}
                    onChange={(e) => {
                      console.log('Cambio deleteFromMikrotik masivo:', e.target.checked);
                      setDeleteFromMikrotik(e.target.checked);
                    }}
                    color="primary"
                    disabled={isDeleting}
                  />
                }
                label="Eliminar también del router MikroTik"
              />
              <Typography variant="caption" color="text.secondary">
                Si desmarca esta opción, los clientes solo se eliminarán de la base de datos pero seguirán existiendo en el router.
              </Typography>
            </FormControl>

            <FormControl component="fieldset" sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={forceDelete}
                    onChange={(e) => {
                      console.log('Cambio forceDelete masivo:', e.target.checked);
                      setForceDelete(e.target.checked);
                    }}
                    color="warning"
                    disabled={isDeleting}
                  />
                }
                label="Forzar eliminación (incluso con facturas, equipos o instalaciones pendientes)"
              />
              <Typography variant="caption" color="error">
                ADVERTENCIA: Al marcar esta opción, se eliminarán los clientes aunque tengan facturas, equipos o instalaciones pendientes. Las facturas serán eliminadas, los equipos se marcarán como devueltos y las instalaciones se cancelarán.
              </Typography>
            </FormControl>

            {errorMessage && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.875rem' }}>
                  {errorMessage}
                </pre>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancel}
            color="primary"
            disabled={isDeleting}
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeleting || selectedClientIds.length === 0}
            startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            {isDeleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Acciones masivas - MEJORADAS
  const handleActionChange = (event) => {
    setSelectedAction(event.target.value);
  };

  const executeAction = async () => {
    if (!selectedAction || selectedClientIds.length === 0 || actionInProgress) return;

    if (selectedAction === 'delete') {
      setMassDeleteDialogOpen(true);
      return;
    }

    const actionText = {
      'activate': 'quitar el corte y activar',
      'deactivate': 'poner corte y desactivar',
      'add_warning': 'poner aviso de vencimiento a',
      'remove_warning': 'quitar aviso de vencimiento de'
    }[selectedAction] || 'procesar';

    if (!window.confirm(`¿Está seguro de que desea ${actionText} los ${selectedClientIds.length} clientes seleccionados?`)) {
      return;
    }

    setActionInProgress(true);
    setOperationProgress({
      show: true,
      message: `${actionText.charAt(0).toUpperCase() + actionText.slice(1)}ando clientes...`,
      current: 0,
      total: selectedClientIds.length
    });

    try {
      console.log('Ejecutando acción masiva:', { clientIds: selectedClientIds, action: selectedAction });

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minutos

      const response = await fetch('/api/clients/mass-action', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          clientIds: selectedClientIds,
          action: selectedAction
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      let responseData;
      try {
        responseData = await response.json();
      } catch (parseError) {
        responseData = { message: 'Error en la respuesta del servidor' };
      }

      if (!response.ok) {
        if (response.status === 408) {
          throw new Error('La operación tardó demasiado tiempo. Intente con menos clientes.');
        }
        throw new Error(responseData.message || `Error ${response.status}: ${response.statusText}`);
      }

      if (selectedAction === 'activate' || selectedAction === 'deactivate') {
        const newStatus = selectedAction === 'activate' ? 'active' : 'inactive';
        const newWarningType = selectedAction === 'activate' ? null : 'corte';
        setClients(prev => prev.map(c =>
          selectedClientIds.includes(c.id) ? { ...c, status: newStatus, warningType: newWarningType } : c
        ));
        setFilteredClients(prev => prev.map(c =>
          selectedClientIds.includes(c.id) ? { ...c, status: newStatus, warningType: newWarningType } : c
        ));
      } else if (selectedAction === 'add_warning') {
        setClients(prev => prev.map(c =>
          selectedClientIds.includes(c.id) ? { ...c, warningType: 'vencimiento' } : c
        ));
        setFilteredClients(prev => prev.map(c =>
          selectedClientIds.includes(c.id) ? { ...c, warningType: 'vencimiento' } : c
        ));
      } else if (selectedAction === 'remove_warning') {
        setClients(prev => prev.map(c =>
          selectedClientIds.includes(c.id) ? { ...c, warningType: null } : c
        ));
        setFilteredClients(prev => prev.map(c =>
          selectedClientIds.includes(c.id) ? { ...c, warningType: null } : c
        ));
      }

      let successMessage = 'Acción masiva completada exitosamente';
      if (responseData.processingTime) {
        successMessage += `\nTiempo: ${responseData.processingTime}ms`;
      }
      if (responseData.mikrotikResults) {
        const errors = responseData.mikrotikResults.filter(r => r.result?.error);
        if (errors.length > 0) {
          successMessage += `\nAdvertencias MikroTik en ${errors.length} clientes`;
        }
      }

      showAlert(successMessage, 'success');
      setSelectedAction('');
      setSelectedClientIds([]);
    } catch (error) {
      console.error('Error ejecutando acción masiva:', error);
      showErrorDetails(error, 'acción masiva');
    } finally {
      setActionInProgress(false);
      setOperationProgress({ show: false, message: '', current: 0, total: 0 });
    }
  };

  // Resto de las funciones permanecen igual...
  const handleToggleStatus = async (clientId) => {
    try {
      const client = clients.find(c => c.id === clientId);
      if (!client) throw new Error('Cliente no encontrado');

      const newStatus = client.status === 'active' ? 'inactive' : 'active';

      const response = await fetch(`/api/clients/${clientId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Error al cambiar el estado del cliente');
      }

      const newWarningType = newStatus === 'active' ? null : 'corte';

      setClients(prev => prev.map(c =>
        c.id === clientId ? { ...c, status: newStatus, warningType: newWarningType, warningDismissed: false } : c
      ));

      setFilteredClients(prev => prev.map(c =>
        c.id === clientId ? { ...c, status: newStatus, warningType: newWarningType, warningDismissed: false } : c
      ));

    } catch (error) {
      console.error('Error toggling client status:', error);
      showErrorDetails(error, 'cambio de estado');
    }
  };

  const handleDeleteClient = useCallback((clientId) => {
    console.log('handleDeleteClient llamado con ID:', clientId);
    setSelectedClientForDelete(clientId);
    setDeleteDialogOpen(true);
  }, []);

  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [invoiceModalLoading, setInvoiceModalLoading] = useState(false);
  const [invoiceDefaults, setInvoiceDefaults] = useState(null);
  const [invoiceDocumentType, setInvoiceDocumentType] = useState('recibo');
  const [invoicePaymentDueDate, setInvoicePaymentDueDate] = useState('');
  const [invoiceCutoffDate, setInvoiceCutoffDate] = useState('');
  const [clientForInvoice, setClientForInvoice] = useState(null);

  const handleGenerateDebt = async (clientId) => {
    setClientForInvoice(clientId);
    setInvoiceModalOpen(true);
    setInvoiceModalLoading(true);
    setInvoiceDefaults(null);
    try {
      const response = await fetch(`/api/clients/${clientId}/billing-defaults`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) {
        throw new Error('Error al obtener valores sugeridos de facturación');
      }
      const data = await response.json();
      setInvoiceDefaults(data);
      setInvoiceDocumentType(data.document_type || 'recibo');
      
      const formatToInputDate = (isoStr) => {
        if (!isoStr) return '';
        return isoStr.split('T')[0];
      };
      
      setInvoicePaymentDueDate(formatToInputDate(data.payment_due_date));
      setInvoiceCutoffDate(formatToInputDate(data.cutoff_date));
    } catch (error) {
      console.error('Error loading billing defaults:', error);
      showAlert(error.message || 'Error al cargar sugerencias de facturación', 'error');
      setInvoiceModalOpen(false);
    } finally {
      setInvoiceModalLoading(false);
    }
  };

  const confirmGenerateDebt = async () => {
    if (!clientForInvoice) return;
    setActionInProgress(true);
    try {
      const response = await fetch(`/api/clients/${clientForInvoice}/generate-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          document_type: invoiceDocumentType,
          payment_due_date: invoicePaymentDueDate,
          due_date: invoiceCutoffDate
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Error al generar la deuda');
      }

      showAlert('Factura/Recibo generado exitosamente', 'success');
      setInvoiceModalOpen(false);
      fetchClients();
    } catch (error) {
      console.error('Error generating manual invoice:', error);
      showErrorDetails(error, 'generación de deuda');
    } finally {
      setActionInProgress(false);
    }
  };

  const handleSendBroadcast = async () => {
    if (broadcasting) return;

    setBroadcasting(true);
    setBroadcastResult(null);

    try {
      const response = await fetch('/api/clients/broadcast-portal', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          channel: broadcastChannel,
          testMode: broadcastTestMode,
          customMessage: broadcastMessage
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al procesar la difusión');
      }

      setBroadcastResult(data);
      if (data.success) {
        showAlert(data.message, 'success');
      } else {
        showAlert(data.message, 'warning');
      }
    } catch (error) {
      console.error('Error broadcasting portal:', error);
      showAlert(error.message || 'Ocurrió un error al procesar la difusión', 'error');
    } finally {
      setBroadcasting(false);
    }
  };

  const handleViewTraffic = (clientId) => {
    navigate(`/clients/traffic?client=${clientId}`);
  };

  const handleViewInvoices = (clientId) => {
    navigate(`/finance/invoices?clientId=${clientId}`);
  };

  // Manejo de ordenamiento
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedClients = useCallback(() => {
    if (!sortConfig.key) return filteredClients;

    return [...filteredClients].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Caso especial para AP / Sectorial
      if (sortConfig.key === 'accessPoint') {
        aVal = a.accessPointName || '';
        bVal = b.accessPointName || '';
      }

      // Caso especial para balance (numérico)
      if (sortConfig.key === 'balance') {
        const aNum = parseFloat(a.balance || 0);
        const bNum = parseFloat(b.balance || 0);
        return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Caso especial para IP Address (numérico por octetos)
      if (sortConfig.key === 'ipAddress') {
        const ipToParts = (ip) => {
          if (!ip) return [0, 0, 0, 0];
          const cleanIp = ip.split('/')[0].trim();
          const parts = cleanIp.split('.').map(p => parseInt(p, 10) || 0);
          while (parts.length < 4) parts.push(0);
          return parts;
        };

        const aParts = ipToParts(aVal);
        const bParts = ipToParts(bVal);

        for (let i = 0; i < 4; i++) {
          if (aParts[i] !== bParts[i]) {
            return sortConfig.direction === 'asc' ? aParts[i] - bParts[i] : bParts[i] - aParts[i];
          }
        }
        return 0;
      }

      // Conversión a string para ordenamiento seguro (case insensitive)
      const aStr = String(aVal || '').toLowerCase();
      const bStr = String(bVal || '').toLowerCase();

      if (aStr < bStr) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aStr > bStr) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredClients, sortConfig]);

  const sortedClients = getSortedClients();

  // Manejo de paginación
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const pageClients = sortedClients.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const numSelected = selectedClientIds.filter(id =>
    pageClients.some(client => client.id === id)
  ).length;
  const isPageSelected = pageClients.length > 0 && numSelected === pageClients.length;

  // ExportClientsDialog has been moved to the bottom of the file (outer scope) to prevent unneeded DOM reconciliations and page-refresh feels when clicking checkboxes.

  if (loading) {
    return (
      <Box className="p-6 flex justify-center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 1.5, md: 2 } }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5, mb: 1.5 }}>
        <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px' }}>Lista de Clientes</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setAddClientModalOpen(true)}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 700,
              px: { xs: 1, sm: 2 },
              height: { xs: '40px', sm: '36px' },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              backgroundColor: '#6366f1',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#4f46e5' },
              width: { xs: 'calc(50% - 4px)', sm: 'auto' }
            }}
          >
            Agregar Cliente
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ImportMikrotikIcon />}
            onClick={() => navigate('/clients/import-mikrotik')}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 700,
              px: { xs: 1, sm: 2 },
              height: { xs: '40px', sm: '36px' },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              borderColor: 'rgba(168, 85, 247, 0.4)',
              color: '#a855f7',
              '&:hover': {
                borderColor: '#a855f7',
                backgroundColor: 'rgba(168, 85, 247, 0.05)'
              },
              width: { xs: 'calc(50% - 4px)', sm: 'auto' }
            }}
          >
            Importar de MikroTik
          </Button>
          <Button
            variant="outlined"
            color="info"
            startIcon={<SendIcon />}
            onClick={() => {
              setBroadcastModalOpen(true);
              setBroadcastResult(null);
            }}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 700,
              px: { xs: 1, sm: 2 },
              height: { xs: '40px', sm: '36px' },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              borderColor: 'rgba(14, 165, 233, 0.4)',
              color: '#0ea5e9',
              '&:hover': {
                borderColor: '#0ea5e9',
                backgroundColor: 'rgba(14, 165, 233, 0.05)'
              },
              width: { xs: 'calc(50% - 4px)', sm: 'auto' }
            }}
          >
            Difundir Portal
          </Button>
          <Button
            variant="outlined"
            color="success"
            startIcon={<ExportIcon />}
            onClick={() => setExportModalOpen(true)}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 700,
              px: { xs: 1, sm: 2 },
              height: { xs: '40px', sm: '36px' },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              borderColor: 'rgba(16, 185, 129, 0.4)',
              color: '#10b981',
              '&:hover': {
                borderColor: '#10b981',
                backgroundColor: 'rgba(16, 185, 129, 0.05)'
              },
              width: { xs: 'calc(50% - 4px)', sm: 'auto' }
            }}
          >
            Exportar Reporte
          </Button>
        </Box>
      </Box>


      {/* Indicador de progreso global */}
      {operationProgress.show && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" gutterBottom sx={{ fontSize: '0.75rem' }}>
            {operationProgress.message}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={(operationProgress.current / operationProgress.total) * 100}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
      )}

      {/* Barra de filtros y acciones masivas unificada */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1.5, mb: 1.5, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' } }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography sx={{ 
            fontWeight: 700, 
            fontSize: '0.75rem', 
            color: selectedClientIds.length > 0 ? '#6366f1' : '#64748b', 
            backgroundColor: selectedClientIds.length > 0 ? 'rgba(99, 102, 241, 0.08)' : 'rgba(0, 0, 0, 0.03)', 
            px: 1.5, 
            height: '36px',
            borderRadius: '8px', 
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `1px solid ${selectedClientIds.length > 0 ? 'rgba(99, 102, 241, 0.15)' : 'rgba(0, 0, 0, 0.05)'}`,
            minWidth: 110,
            textAlign: 'center'
          }}>
            {selectedClientIds.length > 0 ? `${selectedClientIds.length} selec.` : 'Ninguno'}
          </Typography>

          <FormControl variant="outlined" size="small" sx={{ minWidth: 160, '& .MuiOutlinedInput-root': { height: '36px', borderRadius: '8px' } }}>
            <Select
              value={selectedAction}
              onChange={handleActionChange}
              displayEmpty
              sx={{ backgroundColor: 'white', fontSize: '0.8125rem' }}
            >
              <MenuItem value="" disabled sx={{ fontSize: '0.8125rem' }}>Acción masiva</MenuItem>
              <MenuItem value="activate" sx={{ fontSize: '0.8125rem' }}>Quitar corte (Activar)</MenuItem>
              <MenuItem value="deactivate" sx={{ fontSize: '0.8125rem' }}>Poner corte (Desactivar)</MenuItem>
              <MenuItem value="add_warning" sx={{ fontSize: '0.8125rem' }}>🔔 Poner aviso venc.</MenuItem>
              <MenuItem value="remove_warning" sx={{ fontSize: '0.8125rem' }}>🔕 Quitar aviso venc.</MenuItem>
              <MenuItem value="delete" sx={{ fontSize: '0.8125rem' }}>Eliminar</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            startIcon={actionInProgress ? <CircularProgress size={16} color="inherit" /> : <ExecuteIcon sx={{ fontSize: '18px' }} />}
            disabled={!selectedAction || selectedClientIds.length === 0 || actionInProgress}
            onClick={executeAction}
            color="primary"
            sx={{
              height: '36px',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 700,
              px: 2,
              backgroundColor: '#6366f1',
              boxShadow: 'none',
              fontSize: '0.8125rem',
              '&:hover': { backgroundColor: '#4f46e5' },
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            {actionInProgress ? 'Ejecutando...' : 'Ejecutar'}
          </Button>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', md: 'auto' } }}>
          <TextField
            placeholder="Buscar cliente..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#64748b', fontSize: '18px' }} />
                </InputAdornment>
              ),
            }}
            sx={{ 
              flex: 1, 
              minWidth: 160, 
              maxWidth: { md: 240 }, 
              '& .MuiOutlinedInput-root': { 
                height: '36px', 
                borderRadius: '8px', 
                backgroundColor: 'white',
                fontSize: '0.8125rem'
              } 
            }}
          />

          <FormControl variant="outlined" size="small" sx={{ minWidth: 150, '& .MuiOutlinedInput-root': { height: '36px', borderRadius: '8px' } }}>
            <Select
              value={selectedZone}
              onChange={handleZoneChange}
              displayEmpty
              sx={{
                backgroundColor: 'white',
                fontSize: '0.8125rem',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0,0,0,0.08)',
                },
              }}
            >
              <MenuItem value="" sx={{ fontSize: '0.8125rem' }}>Todas las zonas</MenuItem>
              {zones.map((zone) => (
                <MenuItem key={zone.id} value={zone.id} sx={{ fontSize: '0.8125rem' }}>
                  {zone.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Botón de configuración de columnas */}
          <Tooltip title="Configurar columnas visibles">
            <IconButton
              onClick={(e) => setColumnSettingsAnchor(e.currentTarget)}
              sx={{
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: '8px',
                backgroundColor: columnSettingsAnchor ? 'rgba(99, 102, 241, 0.08)' : 'white',
                color: columnSettingsAnchor ? '#6366f1' : '#64748b',
                width: 36,
                height: 36,
                flexShrink: 0,
                '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.08)', color: '#6366f1' }
              }}
            >
              <ViewColumnIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Popover de configuración de columnas */}
        <Menu
          anchorEl={columnSettingsAnchor}
          open={Boolean(columnSettingsAnchor)}
          onClose={() => setColumnSettingsAnchor(null)}
          PaperProps={{
            elevation: 3,
            sx: {
              borderRadius: '12px',
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              minWidth: 240,
              maxHeight: 420,
              p: 0
            }
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '13px' }}>Columnas Visibles</Typography>
            <Button size="small" onClick={handleResetColumns} sx={{ textTransform: 'none', fontSize: '11px', color: '#6366f1', fontWeight: 600, minWidth: 'auto', px: 1 }}>Restablecer</Button>
          </Box>
          <Box sx={{ py: 0.5 }}>
            {ALL_COLUMNS.map(col => (
              <MenuItem
                key={col.id}
                onClick={() => handleColumnToggle(col.id)}
                disabled={col.alwaysVisible}
                dense
                sx={{ py: 0.5, px: 2, gap: 1 }}
              >
                <Checkbox
                  checked={isColumnVisible(col.id)}
                  disabled={col.alwaysVisible}
                  size="small"
                  sx={{ p: 0, mr: 1 }}
                />
                <Typography variant="body2" sx={{ fontSize: '13px', color: col.alwaysVisible ? '#94a3b8' : '#334155' }}>
                  {col.label}
                </Typography>
              </MenuItem>
            ))}
          </Box>
        </Menu>
      </Box>

      {/* Tabla de clientes - RESTO DEL COMPONENTE PERMANECE IGUAL */}
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Cabecera multi-selección móvil */}
          <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5 }}>
            <Checkbox
              indeterminate={numSelected > 0 && numSelected < pageClients.length}
              checked={isPageSelected}
              onChange={handleSelectAllClick}
              size="small"
              sx={{ mr: 1, p: 0 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium' }}>
              Seleccionar todos en esta página
            </Typography>
          </Box>

          {pageClients.map((client) => {
            const isItemSelected = isSelected(client.id);
            return (
              <Paper 
                key={client.id} 
                sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  borderLeft: `3px solid ${client.status === 'active' ? '#4caf50' : '#f44336'}`,
                  backgroundColor: isItemSelected ? '#e3f2fd' : 'white',
                  transition: 'background-color 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }} 
                elevation={1}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, mr: 1 }}>
                    <Checkbox
                      checked={isItemSelected}
                      onChange={() => handleClientSelect(client.id)}
                      color="primary"
                      size="small"
                      sx={{ p: 0, mr: 1 }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem', truncate: true, lineHeight: 1.2 }}>
                      {client.fullName}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <Chip
                      label={client.status === 'active' ? 'Activo' : 'Inactivo'}
                      color={client.status === 'active' ? 'success' : 'error'}
                      size="small"
                      sx={{ height: 18, fontSize: '0.65rem', px: 0.5 }}
                    />
                    {client.warningType && !client.warningDismissed && (
                      <Chip
                        label={
                          client.warningType === 'vencimiento' ? '⚠' :
                          client.warningType === 'corte' ? '🔴' : '⏳'
                        }
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.6rem',
                          px: 0.3,
                          minWidth: 'auto',
                          backgroundColor:
                            client.warningType === 'vencimiento' ? 'rgba(245, 158, 11, 0.15)' :
                            client.warningType === 'corte' ? 'rgba(239, 68, 68, 0.15)' :
                            'rgba(59, 130, 246, 0.15)',
                          color:
                            client.warningType === 'vencimiento' ? '#d97706' :
                            client.warningType === 'corte' ? '#dc2626' :
                            '#2563eb',
                        }}
                      />
                    )}
                  </Box>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, mb: 0.75, fontSize: '0.725rem', lineHeight: 1.3, color: 'text.secondary' }}>
                  <Box sx={{ display: 'flex', gap: 0.5, minWidth: 0 }}>
                    <span style={{ color: '#888', fontWeight: 'bold' }}>IP:</span>
                    <span style={{ color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{client.ipAddress || '-'}</span>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, minWidth: 0 }}>
                    <span style={{ color: '#888', fontWeight: 'bold' }}>Plan:</span>
                    <span style={{ color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{client.planName || '-'}</span>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, minWidth: 0 }}>
                    <span style={{ color: '#888', fontWeight: 'bold' }}>Router:</span>
                    <span style={{ color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{client.routerName || '-'}</span>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, minWidth: 0 }}>
                    <span style={{ color: '#888', fontWeight: 'bold' }}>Zona:</span>
                    <span style={{ color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{client.zoneName || '-'}</span>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, minWidth: 0 }}>
                    <span style={{ color: '#888', fontWeight: 'bold' }}>AP / Sectorial:</span>
                    <span style={{ color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{client.accessPointName ? `${client.accessPointName}${client.nodeName ? ` (${client.nodeName})` : ''}` : '-'}</span>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, minWidth: 0 }}>
                    <span style={{ color: '#888', fontWeight: 'bold' }}>Queue:</span>
                    <span style={{ color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{client.queueName || '-'}</span>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, minWidth: 0, gridColumn: 'span 2' }}>
                    <span style={{ color: '#888', fontWeight: 'bold' }}>Saldo a Favor:</span>
                    <span style={{ color: parseFloat(client.balance || 0) > 0 ? '#10b981' : '#333', fontWeight: parseFloat(client.balance || 0) > 0 ? 'bold' : 'normal' }}>
                      S/ {parseFloat(client.balance || 0).toFixed(2)}
                    </span>
                  </Box>
                </Box>
                
                {client.address && (
                  <Box sx={{ fontSize: '0.725rem', mb: 1, color: 'text.secondary', display: 'flex', gap: 0.5, minWidth: 0 }}>
                    <span style={{ color: '#888', fontWeight: 'bold' }}>Dir:</span>
                    <span style={{ color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{client.address}</span>
                  </Box>
                )}
                {client.coordinates && client.coordinates !== '-' && (
                  <Box sx={{ fontSize: '0.725rem', mb: 1, color: 'text.secondary', display: 'flex', gap: 0.5, minWidth: 0 }}>
                    <span style={{ color: '#888', fontWeight: 'bold' }}>GPS:</span>
                    <a 
                      href={`https://www.google.com/maps/search/?api=1&query=${client.coordinates}`} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      style={{ color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}
                    >
                      📍 {client.coordinates}
                    </a>
                  </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', pt: 0.5, borderTop: '1px solid #f0f0f0' }}>
                  <Tooltip title="Editar">
                    <IconButton 
                      size="small" 
                      onClick={() => navigate(`/clients/edit/${client.id}`)} 
                      color="primary" 
                      sx={{ 
                        p: 0.5,
                        backgroundColor: 'rgba(99, 102, 241, 0.06)',
                        '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.12)' }
                      }}
                    >
                      <EditIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Ver Tráfico">
                    <IconButton 
                      size="small" 
                      onClick={() => handleViewTraffic(client.id)} 
                      color="info" 
                      sx={{ 
                        p: 0.5,
                        backgroundColor: 'rgba(6, 182, 212, 0.06)',
                        '&:hover': { backgroundColor: 'rgba(6, 182, 212, 0.12)' }
                      }}
                    >
                      <TrafficIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Más opciones">
                    <IconButton 
                      size="small" 
                      onClick={(e) => handleActionMenuOpen(e, client)} 
                      color="default" 
                      sx={{ 
                        p: 0.5,
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' }
                      }}
                    >
                      <MoreIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>
            );
          })}

          {filteredClients.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              No se encontraron clientes
            </Paper>
          )}

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredClients.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{ borderTop: '1px solid #e0e0e0', mt: 1 }}
          />
        </Box>
      ) : (
        <Paper 
          elevation={1} 
          sx={{ 
            borderRadius: '16px', 
            overflow: 'hidden', 
            border: '1px solid rgba(0, 0, 0, 0.05)', 
            boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
            backgroundColor: 'white'
          }}
        >
          <TableContainer sx={{ 
            maxHeight: 'calc(100vh - 280px)',
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              height: '6px',
              width: '6px'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(99, 102, 241, 0.2)',
              borderRadius: '4px'
            }
          }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ backgroundColor: '#f8fafc', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                    <Checkbox
                      indeterminate={numSelected > 0 && numSelected < pageClients.length}
                      checked={isPageSelected}
                      onChange={handleSelectAllClick}
                      size="small"
                    />
                  </TableCell>
                  {isColumnVisible('fullName') && (
                    <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 700, color: '#475569', fontSize: '0.8rem', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'fullName'}
                        direction={sortConfig.key === 'fullName' ? sortConfig.direction : 'asc'}
                        onClick={() => requestSort('fullName')}
                        sx={{
                          '&.MuiTableSortLabel-active': { color: '#6366f1' },
                          '&.MuiTableSortLabel-active .MuiTableSortLabel-icon': { color: '#6366f1 !important' }
                        }}
                      >
                        Nombres
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('queueName') && (
                    <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 700, color: '#475569', fontSize: '0.8rem', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'queueName'}
                        direction={sortConfig.key === 'queueName' ? sortConfig.direction : 'asc'}
                        onClick={() => requestSort('queueName')}
                        sx={{
                          '&.MuiTableSortLabel-active': { color: '#6366f1' },
                          '&.MuiTableSortLabel-active .MuiTableSortLabel-icon': { color: '#6366f1 !important' }
                        }}
                      >
                        Queue
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('ipAddress') && (
                    <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 700, color: '#475569', fontSize: '0.8rem', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'ipAddress'}
                        direction={sortConfig.key === 'ipAddress' ? sortConfig.direction : 'asc'}
                        onClick={() => requestSort('ipAddress')}
                        sx={{
                          '&.MuiTableSortLabel-active': { color: '#6366f1' },
                          '&.MuiTableSortLabel-active .MuiTableSortLabel-icon': { color: '#6366f1 !important' }
                        }}
                      >
                        IP
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('status') && (
                    <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 700, color: '#475569', fontSize: '0.8rem', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'status'}
                        direction={sortConfig.key === 'status' ? sortConfig.direction : 'asc'}
                        onClick={() => requestSort('status')}
                        sx={{
                          '&.MuiTableSortLabel-active': { color: '#6366f1' },
                          '&.MuiTableSortLabel-active .MuiTableSortLabel-icon': { color: '#6366f1 !important' }
                        }}
                      >
                        Estado
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('balance') && (
                    <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 700, color: '#475569', fontSize: '0.8rem', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'balance'}
                        direction={sortConfig.key === 'balance' ? sortConfig.direction : 'asc'}
                        onClick={() => requestSort('balance')}
                        sx={{
                          '&.MuiTableSortLabel-active': { color: '#6366f1' },
                          '&.MuiTableSortLabel-active .MuiTableSortLabel-icon': { color: '#6366f1 !important' }
                        }}
                      >
                        Saldo a Favor
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('planName') && (
                    <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 700, color: '#475569', fontSize: '0.8rem', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'planName'}
                        direction={sortConfig.key === 'planName' ? sortConfig.direction : 'asc'}
                        onClick={() => requestSort('planName')}
                        sx={{
                          '&.MuiTableSortLabel-active': { color: '#6366f1' },
                          '&.MuiTableSortLabel-active .MuiTableSortLabel-icon': { color: '#6366f1 !important' }
                        }}
                      >
                        Plan
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('routerName') && (
                    <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 700, color: '#475569', fontSize: '0.8rem', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'routerName'}
                        direction={sortConfig.key === 'routerName' ? sortConfig.direction : 'asc'}
                        onClick={() => requestSort('routerName')}
                        sx={{
                          '&.MuiTableSortLabel-active': { color: '#6366f1' },
                          '&.MuiTableSortLabel-active .MuiTableSortLabel-icon': { color: '#6366f1 !important' }
                        }}
                      >
                        Router
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('zoneName') && (
                    <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 700, color: '#475569', fontSize: '0.8rem', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'zoneName'}
                        direction={sortConfig.key === 'zoneName' ? sortConfig.direction : 'asc'}
                        onClick={() => requestSort('zoneName')}
                        sx={{
                          '&.MuiTableSortLabel-active': { color: '#6366f1' },
                          '&.MuiTableSortLabel-active .MuiTableSortLabel-icon': { color: '#6366f1 !important' }
                        }}
                      >
                        Zona
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('accessPoint') && (
                    <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 700, color: '#475569', fontSize: '0.8rem', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'accessPoint'}
                        direction={sortConfig.key === 'accessPoint' ? sortConfig.direction : 'asc'}
                        onClick={() => requestSort('accessPoint')}
                        sx={{
                          '&.MuiTableSortLabel-active': { color: '#6366f1' },
                          '&.MuiTableSortLabel-active .MuiTableSortLabel-icon': { color: '#6366f1 !important' }
                        }}
                      >
                        AP / Sectorial
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('address') && (
                    <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 700, color: '#475569', fontSize: '0.8rem', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'address'}
                        direction={sortConfig.key === 'address' ? sortConfig.direction : 'asc'}
                        onClick={() => requestSort('address')}
                        sx={{
                          '&.MuiTableSortLabel-active': { color: '#6366f1' },
                          '&.MuiTableSortLabel-active .MuiTableSortLabel-icon': { color: '#6366f1 !important' }
                        }}
                      >
                        Dirección
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {isColumnVisible('coordinates') && (
                    <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 700, color: '#475569', fontSize: '0.8rem', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'coordinates'}
                        direction={sortConfig.key === 'coordinates' ? sortConfig.direction : 'asc'}
                        onClick={() => requestSort('coordinates')}
                        sx={{
                          '&.MuiTableSortLabel-active': { color: '#6366f1' },
                          '&.MuiTableSortLabel-active .MuiTableSortLabel-icon': { color: '#6366f1 !important' }
                        }}
                      >
                        Coordenadas
                      </TableSortLabel>
                    </TableCell>
                  )}
                  <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 700, color: '#475569', fontSize: '0.8rem', borderBottom: '1px solid rgba(0,0,0,0.06)', textAlign: 'center' }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageClients
                  .map((client) => {
                    const isItemSelected = isSelected(client.id);

                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        selected={isItemSelected}
                        key={client.id}
                        sx={{ 
                          transition: 'all 0.2s ease',
                          '&.Mui-selected': { backgroundColor: 'rgba(99, 102, 241, 0.08) !important' },
                          '&:hover': { backgroundColor: '#f8fafc' }
                        }}
                      >
                        <TableCell padding="checkbox" sx={{ py: 0.8 }}>
                          <Checkbox
                            checked={isItemSelected}
                            onChange={() => handleClientSelect(client.id)}
                            color="primary"
                            size="small"
                          />
                        </TableCell>
                        {isColumnVisible('fullName') && (
                          <TableCell sx={{ py: 0.8, fontWeight: 700, color: '#1e293b', fontSize: '0.825rem' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <span>{client.fullName}</span>
                              {(client.is_courtesy || client.isCourtesy) && (
                                <Tooltip title={(client.courtesy_reason || client.courtesyReason) ? `Servicio Donado - Motivo: ${client.courtesy_reason || client.courtesyReason}` : "Servicio Donado / Cortesía (Cobro S/ 0.00)"}>
                                  <Chip
                                    label="🎁 Cortesía"
                                    size="small"
                                    sx={{
                                      backgroundColor: '#f3e8ff',
                                      color: '#7e22ce',
                                      border: '1px solid #c084fc',
                                      fontWeight: 800,
                                      borderRadius: '8px',
                                      fontSize: '0.675rem',
                                      height: '22px'
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        )}
                        {isColumnVisible('queueName') && <TableCell sx={{ py: 0.8, fontFamily: 'monospace', color: '#475569', fontSize: '0.8rem' }}>{client.queueName}</TableCell>}
                        {isColumnVisible('ipAddress') && <TableCell sx={{ py: 0.8, fontFamily: 'monospace', fontWeight: 600, color: '#0f172a', fontSize: '0.8rem' }}>{client.ipAddress}</TableCell>}
                        {isColumnVisible('status') && <TableCell sx={{ py: 0.8 }}>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Chip
                              label={client.status === 'active' ? 'Activo' : 'Inactivo'}
                              size="small"
                              sx={{
                                backgroundColor: client.status === 'active' ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                                color: client.status === 'active' ? '#059669' : '#ef4444',
                                border: `1px solid ${client.status === 'active' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)'}`,
                                fontWeight: 700,
                                borderRadius: '8px',
                                fontSize: '0.725rem',
                                height: '22px'
                              }}
                            />
                            {client.warningType && !client.warningDismissed && (
                              <Chip
                                label={
                                  client.warningType === 'vencimiento' ? '⚠ Vencimiento' :
                                  client.warningType === 'corte' ? '🔴 Corte' :
                                  client.warningType === 'prorroga' ? '⏳ Prórroga' : client.warningType
                                }
                                size="small"
                                sx={{
                                  backgroundColor:
                                    client.warningType === 'vencimiento' ? 'rgba(245, 158, 11, 0.1)' :
                                    client.warningType === 'corte' ? 'rgba(239, 68, 68, 0.1)' :
                                    'rgba(59, 130, 246, 0.1)',
                                  color:
                                    client.warningType === 'vencimiento' ? '#d97706' :
                                    client.warningType === 'corte' ? '#dc2626' :
                                    '#2563eb',
                                  border: `1px solid ${
                                    client.warningType === 'vencimiento' ? 'rgba(245, 158, 11, 0.2)' :
                                    client.warningType === 'corte' ? 'rgba(239, 68, 68, 0.2)' :
                                    'rgba(59, 130, 246, 0.2)'
                                  }`,
                                  fontWeight: 700,
                                  borderRadius: '8px',
                                  fontSize: '0.65rem',
                                  height: '20px'
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>}
                        {isColumnVisible('balance') && <TableCell sx={{ py: 0.8, fontWeight: 700, color: parseFloat(client.balance || 0) > 0 ? '#10b981' : '#475569', fontSize: '0.8rem' }}>
                          {parseFloat(client.balance || 0) > 0 ? `S/ ${parseFloat(client.balance).toFixed(2)}` : 'S/ 0.00'}
                        </TableCell>}
                        {isColumnVisible('planName') && <TableCell sx={{ py: 0.8, fontWeight: 600, color: '#334155', fontSize: '0.8rem' }}>{client.planName}</TableCell>}
                        {isColumnVisible('routerName') && <TableCell sx={{ py: 0.8, color: '#64748b', fontSize: '0.8rem', fontWeight: 500 }}>{client.routerName}</TableCell>}
                        {isColumnVisible('zoneName') && <TableCell sx={{ py: 0.8, color: '#64748b', fontSize: '0.8rem', fontWeight: 500 }}>{client.zoneName || '-'}</TableCell>}
                        {isColumnVisible('accessPoint') && <TableCell sx={{ py: 0.8, color: '#64748b', fontSize: '0.8rem', fontWeight: 500 }}>
                          {client.accessPointName ? (
                            <Tooltip title={client.nodeName ? `Nodo: ${client.nodeName}` : ''}>
                              <span>{client.accessPointName}{client.nodeName ? ` (${client.nodeName})` : ''}</span>
                            </Tooltip>
                          ) : '-'}
                        </TableCell>}
                        {isColumnVisible('address') && <TableCell sx={{ py: 0.8, color: '#64748b', fontSize: '0.8rem', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{client.address}</TableCell>}
                        {isColumnVisible('coordinates') && (
                          <TableCell sx={{ py: 0.8, color: '#64748b', fontSize: '0.8rem', fontWeight: 500 }}>
                            {client.coordinates && client.coordinates !== '-' ? (
                              <a 
                                href={`https://www.google.com/maps/search/?api=1&query=${client.coordinates}`} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                style={{ color: '#6366f1', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px', fontWeight: 600 }}
                              >
                                📍 {client.coordinates}
                              </a>
                            ) : '-'}
                          </TableCell>
                        )}
                        <TableCell sx={{ py: 0.8, textAlign: 'center' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/clients/edit/${client.id}`)}
                                sx={{
                                  color: '#6366f1',
                                  backgroundColor: 'rgba(99, 102, 241, 0.06)',
                                  padding: '4px',
                                  '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.12)' }
                                }}
                              >
                                <EditIcon fontSize="small" style={{ fontSize: '0.9rem' }} />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Ver Tráfico">
                              <IconButton
                                size="small"
                                onClick={() => handleViewTraffic(client.id)}
                                sx={{
                                  color: '#06b6d4',
                                  backgroundColor: 'rgba(6, 182, 212, 0.06)',
                                  padding: '4px',
                                  '&:hover': { backgroundColor: 'rgba(6, 182, 212, 0.12)' }
                                }}
                              >
                                <TrafficIcon fontSize="small" style={{ fontSize: '0.9rem' }} />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title={client.status === 'inactive' || client.status === 'baja' ? "Ver Acta de Devolución (Anexo 2)" : "Dar de Baja el Servicio (Anexo 2)"}>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (client.status === 'inactive' || client.status === 'baja') {
                                    setContractClientId(client.id);
                                    setContractInitialTab(2);
                                    setContractModalOpen(true);
                                  } else {
                                    setClientForDeactivation(client);
                                    setDeactivationModalOpen(true);
                                  }
                                }}
                                sx={{
                                  color: client.status === 'inactive' || client.status === 'baja' ? '#ef4444' : '#d97706',
                                  backgroundColor: client.status === 'inactive' || client.status === 'baja' ? 'rgba(239, 68, 68, 0.08)' : 'rgba(217, 119, 6, 0.08)',
                                  padding: '4px',
                                  '&:hover': { backgroundColor: client.status === 'inactive' || client.status === 'baja' ? 'rgba(239, 68, 68, 0.16)' : 'rgba(217, 119, 6, 0.16)' }
                                }}
                              >
                                <BlockIcon fontSize="small" style={{ fontSize: '0.9rem' }} />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Más opciones">
                              <IconButton
                                size="small"
                                onClick={(e) => handleActionMenuOpen(e, client)}
                                sx={{
                                  color: '#64748b',
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                  padding: '4px',
                                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' }
                                }}
                              >
                                <MoreIcon fontSize="small" style={{ fontSize: '0.9rem' }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                {filteredClients.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length + 2} align="center" sx={{ py: 4, color: '#64748b' }}>
                      No se encontraron clientes
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredClients.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}
          />
        </Paper>
      )}

      {/* Modal para Agregar Cliente */}
      <Dialog
        open={addClientModalOpen}
        onClose={() => setAddClientModalOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Agregar Nuevo Cliente</Typography>
          <IconButton
            onClick={() => setAddClientModalOpen(false)}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Elija una opción para continuar:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => {
                setAddClientModalOpen(false);
                const path = selectedZone ? `/clients/add?zone=${selectedZone}` : '/clients/add';
                navigate(path);
              }}
              fullWidth
            >
              Agregar Cliente en Página Completa
            </Button>

            <Button
              variant="outlined"
              startIcon={<EngineeringIcon />}
              onClick={() => {
                setAddClientModalOpen(false);
                navigate('/clients/installations');
              }}
              fullWidth
            >
              Crear desde Instalación Pendiente
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddClientModalOpen(false)}>
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de eliminación masiva */}
      <MassDeleteDialog
        open={massDeleteDialogOpen}
        onClose={() => {
          setMassDeleteDialogOpen(false);
        }}
      />

      {/* Diálogo de eliminación individual */}
      <DeleteClientDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedClientForDelete(null);
        }}
        clientId={selectedClientForDelete}
      />

      {/* Diálogo de exportación personalizada */}
      <ExportClientsDialog
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        selectedExportColumns={selectedExportColumns}
        setSelectedExportColumns={setSelectedExportColumns}
        exportFormat={exportFormat}
        setExportFormat={setExportFormat}
        selectedZone={selectedZone}
        zones={zones}
        searchTerm={searchTerm}
      />

      {/* Diálogo de confirmación para Generar Deuda Manual */}
      <Dialog
        open={invoiceModalOpen}
        onClose={actionInProgress || invoiceModalLoading ? undefined : () => setInvoiceModalOpen(false)}
        PaperProps={{
          sx: { borderRadius: '16px', p: 1 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', pb: 1 }}>
          Confirmar Generación de Recibo / Deuda
        </DialogTitle>
        <DialogContent>
          {invoiceModalLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 4, gap: 2, minWidth: 320 }}>
              <CircularProgress size={40} thickness={4} />
              <Typography variant="body2" color="text.secondary">
                Obteniendo valores sugeridos de facturación...
              </Typography>
            </Box>
          ) : invoiceDefaults ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, minWidth: { xs: 280, sm: 380 }, mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Estás a punto de generar la deuda mensual para el cliente <strong>{invoiceDefaults.client_name}</strong>.
              </Typography>

              <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e293b', mb: 1 }}>
                  Resumen de Importes
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">Plan de Internet:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>S/ {invoiceDefaults.plan_price?.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">Servicios Adicionales:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>S/ {invoiceDefaults.additional_services_total?.toFixed(2)}</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e293b' }}>Total a Facturar:</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#4f46e5' }}>S/ {invoiceDefaults.total_amount?.toFixed(2)}</Typography>
                </Box>
              </Box>

              <FormControl fullWidth>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#334155' }}>
                  Tipo de Comprobante
                </Typography>
                <Select
                  value={invoiceDocumentType}
                  onChange={(e) => setInvoiceDocumentType(e.target.value)}
                  size="small"
                  sx={{ borderRadius: '8px' }}
                >
                  <MenuItem value="recibo">Recibo (Interno)</MenuItem>
                  <MenuItem value="boleta">Boleta de Venta</MenuItem>
                  <MenuItem value="factura">Factura</MenuItem>
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Vencimiento de Pago"
                  type="date"
                  value={invoicePaymentDueDate}
                  onChange={(e) => setInvoicePaymentDueDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  inputProps={{ style: { fontSize: 14 } }}
                />
                <TextField
                  label="Fecha de Corte"
                  type="date"
                  value={invoiceCutoffDate}
                  onChange={(e) => setInvoiceCutoffDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  inputProps={{ style: { fontSize: 14 } }}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#eff6ff', p: 1.5, borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                <Typography variant="caption" color="primary.dark" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  ℹ️ Las fechas se calculan automáticamente con el ciclo de gracia de su zona, pero puedes cambiarlas libremente.
                </Typography>
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="error">
              Error al cargar sugerencias de facturación.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setInvoiceModalOpen(false)}
            variant="outlined"
            disabled={actionInProgress}
            sx={{ borderRadius: '8px', textTransform: 'none' }}
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmGenerateDebt}
            variant="contained"
            color="primary"
            disabled={actionInProgress || invoiceModalLoading || !invoiceDefaults}
            sx={{ borderRadius: '8px', textTransform: 'none', px: 3 }}
          >
            {actionInProgress ? <CircularProgress size={24} color="inherit" /> : 'Confirmar y Generar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de diagnóstico de enlace / Ping */}
      <Dialog
        open={pingDialogOpen}
        onClose={pingLoading ? undefined : () => setPingDialogOpen(false)}
        PaperProps={{
          sx: { borderRadius: '16px', p: 1 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', pb: 1 }}>
          Diagnóstico de Enlace (Ping)
        </DialogTitle>
        <DialogContent>
          {clientForPing && (
            <Box sx={{ minWidth: { xs: 280, sm: 360 } }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#334155' }}>
                Cliente: <span style={{ fontWeight: 'normal' }}>{clientForPing.fullName}</span>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: '#475569' }}>
                <strong>IP del Cliente:</strong> {clientForPing.ipAddress || 'Sin IP configurada'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, color: '#475569' }}>
                <strong>Router:</strong> {clientForPing.routerName || 'Sin router asignado'}
              </Typography>

              {pingLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 3, gap: 2 }}>
                  <CircularProgress size={40} thickness={4} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                    Enviando paquetes de diagnóstico desde el router...
                  </Typography>
                </Box>
              ) : pingResult ? (
                <Box sx={{ 
                  p: 2, 
                  borderRadius: '12px', 
                  backgroundColor: pingResult.online ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                  border: `1px solid ${pingResult.online ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)'}`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1.5,
                  my: 2
                }}>
                  <Box sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: pingResult.online ? '#10b981' : '#ef4444',
                    color: 'white',
                    fontSize: '1.5rem',
                    fontWeight: 'bold'
                  }}>
                    {pingResult.online ? '✓' : '✗'}
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: pingResult.online ? '#059669' : '#b91c1c' }}>
                    {pingResult.online ? 'Enlace Activo' : 'Sin Respuesta'}
                  </Typography>
                  <Typography variant="body2" align="center" color="text.secondary" sx={{ fontSize: '0.825rem' }}>
                    {pingResult.message}
                  </Typography>
                </Box>
              ) : null}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setPingDialogOpen(false)} 
            disabled={pingLoading}
            variant="outlined"
            sx={{ borderRadius: '8px', textTransform: 'none' }}
          >
            Cerrar
          </Button>
          {pingResult && (
            <Button 
              onClick={() => handlePingClient(clientForPing)} 
              disabled={pingLoading}
              variant="contained"
              sx={{ borderRadius: '8px', textTransform: 'none', backgroundColor: '#6366f1', '&:hover': { backgroundColor: '#4f46e5' } }}
            >
              Reintentar
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Menú de Acciones Compartido */}
      <Menu
        anchorEl={actionMenuAnchorEl}
        open={Boolean(actionMenuAnchorEl)}
        onClose={handleActionMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 180,
            borderRadius: '12px',
            border: '1px solid rgba(0,0,0,0.06)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }
        }}
      >
        {actionMenuClient && (
          <>
            <MenuItem 
              onClick={() => {
                handleToggleStatus(actionMenuClient.id);
                handleActionMenuClose();
              }}
              sx={{ gap: 1.5, fontSize: '0.85rem', fontWeight: 600, py: 1 }}
            >
              {actionMenuClient.status === 'active' ? (
                <>
                  <BlockIcon fontSize="small" sx={{ color: '#d97706' }} />
                  <span>Desactivar Cliente</span>
                </>
              ) : (
                <>
                  <ActiveIcon fontSize="small" sx={{ color: '#10b981' }} />
                  <span>Activar Cliente</span>
                </>
              )}
            </MenuItem>
            
            <MenuItem 
              onClick={() => {
                handleViewInvoices(actionMenuClient.id);
                handleActionMenuClose();
              }}
              sx={{ gap: 1.5, fontSize: '0.85rem', py: 1 }}
            >
              <ReceiptIcon fontSize="small" sx={{ color: '#0ea5e9' }} />
              <span>Ver Facturas</span>
            </MenuItem>

            <MenuItem 
              onClick={() => {
                handlePingClient(actionMenuClient);
                handleActionMenuClose();
              }}
              sx={{ gap: 1.5, fontSize: '0.85rem', py: 1 }}
            >
              <PingIcon fontSize="small" sx={{ color: '#3b82f6' }} />
              <span>Probar Enlace (Ping)</span>
            </MenuItem>

            <MenuItem 
              onClick={() => {
                handleGenerateDebt(actionMenuClient.id);
                handleActionMenuClose();
              }}
              sx={{ gap: 1.5, fontSize: '0.85rem', py: 1 }}
            >
              <DebtIcon fontSize="small" sx={{ color: '#a855f7' }} />
              <span>Generar Deuda</span>
            </MenuItem>

            <MenuItem 
              onClick={() => {
                setContractClientId(actionMenuClient.id);
                setContractInitialTab(0);
                setContractModalOpen(true);
                handleActionMenuClose();
              }}
              sx={{ gap: 1.5, fontSize: '0.85rem', py: 1 }}
            >
              <DescriptionIcon fontSize="small" sx={{ color: '#0ea5e9' }} />
              <span>📜 Ver Contrato OSIPTEL (Anexo 1)</span>
            </MenuItem>

            {actionMenuClient.status === 'inactive' || actionMenuClient.status === 'baja' ? (
              <MenuItem 
                onClick={() => {
                  setContractClientId(actionMenuClient.id);
                  setContractInitialTab(2);
                  setContractModalOpen(true);
                  handleActionMenuClose();
                }}
                sx={{ gap: 1.5, fontSize: '0.85rem', color: '#dc2626', fontWeight: 700, py: 1 }}
              >
                <DescriptionIcon fontSize="small" sx={{ color: '#dc2626' }} />
                <span>📦 Ver Acta de Devolución (Anexo 2)</span>
              </MenuItem>
            ) : (
              <MenuItem 
                onClick={() => {
                  setClientForDeactivation(actionMenuClient);
                  setDeactivationModalOpen(true);
                  handleActionMenuClose();
                }}
                sx={{ gap: 1.5, fontSize: '0.85rem', color: '#d97706', fontWeight: 600, py: 1 }}
              >
                <BlockIcon fontSize="small" sx={{ color: '#d97706' }} />
                <span>⚠️ Registrar Baja de Servicio (Anexo 2)</span>
              </MenuItem>
            )}

            <MenuItem 
              onClick={() => {
                const path = `/clients/add?cloneFrom=${actionMenuClient.id}`;
                navigate(path);
                handleActionMenuClose();
              }}
              sx={{ gap: 1.5, fontSize: '0.85rem', py: 1 }}
            >
              <AddIcon fontSize="small" sx={{ color: '#6366f1' }} />
              <span>Agregar Nuevo Punto / Contrato</span>
            </MenuItem>

            <Divider sx={{ my: 0.5 }} />

            <MenuItem 
              onClick={() => {
                handleDeleteClient(actionMenuClient.id);
                handleActionMenuClose();
              }}
              sx={{ 
                gap: 1.5, 
                fontSize: '0.85rem', 
                color: '#ef4444', 
                fontWeight: 600, 
                py: 1,
                '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.05)' }
              }}
            >
              <DeleteIcon fontSize="small" />
              <span>Eliminar Cliente</span>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Modal de Contrato del Cliente */}
      <ContractModal
        open={contractModalOpen}
        onClose={() => setContractModalOpen(false)}
        clientId={contractClientId}
        initialTab={contractInitialTab}
        onOpenDeactivation={(client) => {
          setClientForDeactivation(client);
          setDeactivationModalOpen(true);
        }}
      />

      {/* Diálogo de Baja de Servicio (Anexo 2) */}
      <DeactivationModal
        open={deactivationModalOpen}
        onClose={() => {
          setDeactivationModalOpen(false);
          setClientForDeactivation(null);
        }}
        client={clientForDeactivation}
        onSuccess={(id) => {
          fetchClients();
          setContractClientId(id);
          setContractInitialTab(2);
          setContractModalOpen(true);
        }}
      />

      {/* Diálogo de Difusión de Portal */}
      <Dialog
        open={broadcastModalOpen}
        onClose={() => !broadcasting && setBroadcastModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '16px', p: 1 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', fontFamily: '"Outfit", "Inter", sans-serif', pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Difundir Portal del Cliente</span>
          <IconButton onClick={() => setBroadcastModalOpen(false)} disabled={broadcasting}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {broadcastResult ? (
            <Box sx={{ py: 1 }}>
              <Alert severity={broadcastResult.failedCount === 0 ? "success" : "warning"} sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {broadcastResult.message}
                </Typography>
                {!broadcastResult.isWhatsappConnected && (broadcastChannel === 'whatsapp' || broadcastChannel === 'both') && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 'bold', color: '#b91c1c' }}>
                    ⚠️ Advertencia: El gateway de WhatsApp está desconectado. Los mensajes de WhatsApp no pudieron enviarse.
                  </Typography>
                )}
              </Alert>

              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                Detalles del Envío:
              </Typography>
              
              <Box sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left' }}>
                      <th style={{ padding: '10px 12px' }}>Cliente</th>
                      <th style={{ padding: '10px 12px' }}>Correo</th>
                      <th style={{ padding: '10px 12px' }}>WhatsApp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {broadcastResult.details.map((detail, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px 12px', fontWeight: 'medium' }}>{detail.name}</td>
                        <td style={{ padding: '10px 12px' }}>
                          {broadcastChannel === 'whatsapp' ? (
                            <span style={{ color: '#94a3b8' }}>No enviado (omitido)</span>
                          ) : detail.emailSent ? (
                            <span style={{ color: '#16a34a', fontWeight: 'bold' }}>✓ Enviado</span>
                          ) : (
                            <span style={{ color: '#dc2626' }} title={detail.emailError}>✗ Falló ({detail.emailError || 'Sin correo'})</span>
                          )}
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          {broadcastChannel === 'email' ? (
                            <span style={{ color: '#94a3b8' }}>No enviado (omitido)</span>
                          ) : detail.whatsappSent ? (
                            <span style={{ color: '#16a34a', fontWeight: 'bold' }}>✓ Encolado</span>
                          ) : (
                            <span style={{ color: '#dc2626' }} title={detail.whatsappError}>✗ Falló ({detail.whatsappError || 'Sin teléfono'})</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </Box>
          ) : (
            <Box sx={{ py: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Este módulo permite enviar de forma masiva a sus clientes las credenciales y el enlace de acceso al nuevo Portal del Cliente.
              </Typography>

              {/* Toggle de Modo de Prueba */}
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f0fdf4', borderColor: '#bcf0da', borderRadius: '12px' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={broadcastTestMode}
                      onChange={(e) => setBroadcastTestMode(e.target.checked)}
                      color="success"
                      disabled={broadcasting}
                    />
                  }
                  label={<strong>Modo de Prueba (Recomendado)</strong>}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, pl: 4 }}>
                  Si está activado, la difusión se enviará **únicamente al cliente de prueba Jean Castillo** para comprobar plantillas y funcionamiento. Desmarque para difundir a todos los clientes activos.
                </Typography>
              </Paper>

              {/* Selección de Canal */}
              <FormControl component="fieldset" disabled={broadcasting}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Canal de Difusión:
                </Typography>
                <RadioGroup
                  row
                  value={broadcastChannel}
                  onChange={(e) => setBroadcastChannel(e.target.value)}
                >
                  <FormControlLabel value="both" control={<Radio />} label="Ambos (Correo y WhatsApp)" />
                  <FormControlLabel value="email" control={<Radio />} label="Solo Correo Electrónico" />
                  <FormControlLabel value="whatsapp" control={<Radio />} label="Solo WhatsApp" />
                </RadioGroup>
              </FormControl>

              {/* Mensaje de WhatsApp personalizable */}
              {(broadcastChannel === 'whatsapp' || broadcastChannel === 'both') && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', justifyContent: 'space-between' }}>
                    <span>Plantilla de Mensaje (WhatsApp):</span>
                    <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 'normal' }}>Variables: &#123;client_name&#125;, &#123;link_portal&#125;, &#123;dni&#125;</span>
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    variant="outlined"
                    placeholder="Escriba el mensaje para WhatsApp..."
                    value={broadcastMessage}
                    onChange={(e) => setBroadcastMessage(e.target.value)}
                    disabled={broadcasting}
                    helperText="Si lo deja vacío, se utilizará la plantilla predeterminada de YinoWISP."
                    InputProps={{
                      sx: { fontFamily: 'monospace', fontSize: '0.85rem' }
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          {broadcastResult ? (
            <Button
              onClick={() => {
                setBroadcastResult(null);
                setBroadcastModalOpen(false);
              }}
              variant="contained"
              sx={{ borderRadius: '8px', textTransform: 'none', px: 3 }}
            >
              Finalizar
            </Button>
          ) : (
            <>
              <Button
                onClick={() => setBroadcastModalOpen(false)}
                disabled={broadcasting}
                variant="outlined"
                sx={{ borderRadius: '8px', textTransform: 'none' }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSendBroadcast}
                disabled={broadcasting}
                variant="contained"
                color={broadcastTestMode ? "success" : "primary"}
                startIcon={broadcasting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                sx={{ borderRadius: '8px', textTransform: 'none', px: 3 }}
              >
                {broadcasting ? 'Enviando...' : broadcastTestMode ? 'Enviar a Jean Castillo' : 'Iniciar Difusión Masiva'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Diálogo para Registrar Baja de Servicio y Devolución de Equipos (Anexo 2)
const DeactivationModal = ({ open, onClose, client, onSuccess }) => {
  const [reason, setReason] = useState('Solicitud voluntaria del abonado');
  const [customReason, setCustomReason] = useState('');
  const [notes, setNotes] = useState('');
  const [equipments, setEquipments] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && client) {
      fetch(`/api/client-equipment/client/${client.id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
        .then(r => r.json())
        .then(data => {
          const list = Array.isArray(data) ? data : (data.equipment || []);
          setEquipments(list.map(eq => ({
            equipment_id: eq.equipment_id || eq.id,
            name: eq.name || eq.type || 'Equipo Inalámbrico',
            brand: eq.brand || '',
            model: eq.model || '',
            serial_number: eq.serial_number || eq.mac_address || '',
            return_condition: 'bueno',
            notes: ''
          })));
        })
        .catch(() => setEquipments([]));
    }
  }, [open, client]);

  const handleSubmit = async () => {
    if (!client) return;
    try {
      setSubmitting(true);
      const finalReason = reason === 'Otro' ? customReason : reason;
      const res = await fetch(`/api/clients/${client.id}/deactivation-return`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          reason: finalReason,
          returnedEquipments: equipments,
          notes
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error procesando la baja');

      alert('✅ ¡Baja del servicio registrada exitosamente! Se deshabilitó la cola en MikroTik y se generó el Anexo 2 (Acta de Devolución).');
      onSuccess(client.id);
      onClose();
    } catch (err) {
      alert('⚠️ Error al procesar baja del cliente: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!client) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
      <DialogTitle sx={{ fontWeight: 'bold', color: '#dc2626', display: 'flex', alignItems: 'center', gap: 1 }}>
        <BlockIcon />
        <span>Registrar Baja del Servicio de Internet</span>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2, color: '#334155' }}>
          Estás registrando la baja del servicio para el cliente <strong>{client.fullName}</strong>. Esto deshabilitará su cola en MikroTik y generará el <strong>Anexo 2 (Acta de Devolución de Equipos)</strong>.
        </Typography>

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Motivo de la Baja:</Typography>
          <Select value={reason} onChange={(e) => setReason(e.target.value)} sx={{ borderRadius: '8px' }}>
            <MenuItem value="Solicitud voluntaria del abonado">Solicitud voluntaria del abonado</MenuItem>
            <MenuItem value="Falta de pago / Mora acumulada">Falta de pago / Mora acumulada</MenuItem>
            <MenuItem value="Traslado a zona sin cobertura">Traslado a zona sin cobertura</MenuItem>
            <MenuItem value="Incumplimiento de términos contractuales">Incumplimiento de términos contractuales</MenuItem>
            <MenuItem value="Otro">Otro motivo personalizado</MenuItem>
          </Select>
        </FormControl>

        {reason === 'Otro' && (
          <TextField
            label="Especifique el motivo"
            fullWidth
            size="small"
            value={customReason}
            onChange={(e) => setCustomReason(e.target.value)}
            sx={{ mb: 2 }}
          />
        )}

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#1e293b' }}>
          Equipos en Alquiler / Comodato A Recuperar:
        </Typography>

        {equipments.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2, borderRadius: '8px' }}>
            El cliente no tiene registrados equipos adicionales. Se considerará la Antena CPE y Router principal de su ficha.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
            {equipments.map((eq, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 1.5, borderRadius: '10px', bgcolor: '#f8fafc' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', color: '#0f172a' }}>
                  {eq.name} - {eq.brand} {eq.model} (S/N: {eq.serial_number || 'N/A'})
                </Typography>
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <Select
                    value={eq.return_condition}
                    onChange={(e) => {
                      const updated = [...equipments];
                      updated[idx].return_condition = e.target.value;
                      setEquipments(updated);
                    }}
                    sx={{ borderRadius: '6px', fontSize: '0.8rem', bgcolor: 'white' }}
                  >
                    <MenuItem value="excelente">[✓] Excelente Estado (Reingresar a Stock)</MenuItem>
                    <MenuItem value="bueno">[✓] Bueno / Operativo (Reingresar a Stock)</MenuItem>
                    <MenuItem value="desgaste">[⚠] Desgaste Normal de Uso</MenuItem>
                    <MenuItem value="daño">[❌] Dañado por mal uso / Sobretensión</MenuItem>
                    <MenuItem value="no_devuelto">[⚠️] No Devuelto por el cliente</MenuItem>
                  </Select>
                </FormControl>
              </Paper>
            ))}
          </Box>
        )}

        <TextField
          label="Observaciones Adicionales"
          multiline
          rows={2}
          fullWidth
          size="small"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Ingrese cualquier nota sobre el retiro o devolución de equipos..."
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={submitting} variant="outlined" sx={{ borderRadius: '8px', textTransform: 'none' }}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting}
          variant="contained"
          color="error"
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <BlockIcon />}
          sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}
        >
          {submitting ? 'Procesando Baja...' : 'Confirmar Baja Definitiva'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Componente de diálogo para exportar reportes - PREMIUM (Definición externa para rendimiento)
const ExportClientsDialog = ({
  open,
  onClose,
  selectedExportColumns,
  setSelectedExportColumns,
  exportFormat,
  setExportFormat,
  selectedZone,
  zones,
  searchTerm
}) => {
  const handleToggleColumn = (colId) => {
    setSelectedExportColumns(prev => {
      if (prev.includes(colId)) {
        if (prev.length === 1) return prev; // No permitir vaciar todas
        return prev.filter(id => id !== colId);
      } else {
        return [...prev, colId];
      }
    });
  };

  const handleSelectAll = () => {
    setSelectedExportColumns(ALL_COLUMNS.map(c => c.id));
  };

  const handleSelectNone = () => {
    // Dejar al menos fullName
    setSelectedExportColumns(['fullName']);
  };

  const handleDownload = () => {
    const token = localStorage.getItem('token');
    const cols = selectedExportColumns.join(',');
    const zoneParam = selectedZone ? `&zone=${selectedZone}` : '';
    const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : '';
    const url = `/api/clients/export?format=${exportFormat}&columns=${cols}${zoneParam}${searchParam}&token=${token}`;
    window.open(url, '_blank');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '16px', p: 1 }
      }}
    >
      <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <span>Exportar Reporte de Clientes</span>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ py: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Configure su reporte personalizado. Los filtros activos en la tabla (Zona: <strong>{selectedZone ? zones.find(z => z.id === selectedZone)?.name : 'Todas'}</strong>, Búsqueda: <strong>{searchTerm || 'Ninguna'}</strong>) se aplicarán automáticamente al archivo descargado.
          </Typography>

          {/* Selección de Formato */}
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5, color: '#334155' }}>
            1. Seleccione el Formato:
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3.5 }}>
            <Paper
              variant="outlined"
              onClick={() => setExportFormat('excel')}
              sx={{
                p: 2.5,
                borderRadius: '12px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                borderColor: exportFormat === 'excel' ? '#10b981' : 'divider',
                borderWidth: exportFormat === 'excel' ? '2px' : '1px',
                backgroundColor: exportFormat === 'excel' ? 'rgba(16, 185, 129, 0.04)' : 'transparent',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: '#10b981',
                  backgroundColor: 'rgba(16, 185, 129, 0.02)'
                }
              }}
            >
              <span style={{ fontSize: '2rem' }}>📊</span>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: exportFormat === 'excel' ? '#047857' : '#334155' }}>
                Excel (.xlsx)
              </Typography>
              <Typography variant="caption" color="text.secondary" align="center">
                Ideal para manipulación de datos y cálculos.
              </Typography>
            </Paper>

            <Paper
              variant="outlined"
              onClick={() => setExportFormat('pdf')}
              sx={{
                p: 2.5,
                borderRadius: '12px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                borderColor: exportFormat === 'pdf' ? '#ef4444' : 'divider',
                borderWidth: exportFormat === 'pdf' ? '2px' : '1px',
                backgroundColor: exportFormat === 'pdf' ? 'rgba(239, 68, 68, 0.04)' : 'transparent',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: '#ef4444',
                  backgroundColor: 'rgba(239, 68, 68, 0.02)'
                }
              }}
            >
              <span style={{ fontSize: '2rem' }}>📕</span>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: exportFormat === 'pdf' ? '#b91c1c' : '#334155' }}>
                PDF Documento
              </Typography>
              <Typography variant="caption" color="text.secondary" align="center">
                Formato listo para imprimir y presentar.
              </Typography>
            </Paper>
          </Box>

          {/* Selección de Columnas */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#334155' }}>
              2. Seleccione las Columnas a Incluir:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" onClick={handleSelectAll} sx={{ textTransform: 'none', fontSize: '11px', fontWeight: 600 }}>Todos</Button>
              <Button size="small" onClick={handleSelectNone} sx={{ textTransform: 'none', fontSize: '11px', fontWeight: 600 }} color="warning">Ninguno</Button>
            </Box>
          </Box>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', bgcolor: '#f8fafc' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
              {ALL_COLUMNS.map(col => (
                <FormControlLabel
                  key={col.id}
                  control={
                    <Checkbox
                      checked={selectedExportColumns.includes(col.id)}
                      onChange={() => handleToggleColumn(col.id)}
                      size="small"
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: '#334155' }}>
                      {col.label}
                    </Typography>
                  }
                />
              ))}
            </Box>
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: '8px', textTransform: 'none' }}>
          Cancelar
        </Button>
        <Button
          onClick={handleDownload}
          variant="contained"
          color={exportFormat === 'excel' ? 'success' : 'error'}
          startIcon={<ExportIcon />}
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            px: 3,
            backgroundColor: exportFormat === 'excel' ? '#10b981' : '#ef4444',
            '&:hover': {
              backgroundColor: exportFormat === 'excel' ? '#059669' : '#dc2626'
            }
          }}
        >
          Descargar Reporte
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientList;